import { PlusIcon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { Button, Tooltip } from '@/design-system';
import { PromptTool, isPlaygroundTools } from '@/utils/ai-tools';
import { pluralize } from '@/utils/stringUtils';

import { getModelForPrompt } from '../../utils/Playground.utils';
import { useDeep, usePlaygroundStore } from '../hooks/usePlaygroundStore';
import { ToolModal } from './ToolModal';

export const PlaygroundToolsButton = ({
  index,
  hasOutputSchema,
  toolsSupportedForModel,
}: {
  index: number;
  hasOutputSchema: boolean;
  toolsSupportedForModel: boolean;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const setEdited = usePlaygroundStore((state) => state.setEdited);
  const setOptions = usePlaygroundStore((state) => state.setOptions);
  const options = usePlaygroundStore(
    useDeep((state) => state.prompts[index].options)
  );
  const currentPromptTools = options.tools;
  const tools = isPlaygroundTools(currentPromptTools)
    ? currentPromptTools.toReversed()
    : [];
  const setTools = (tools: Array<PromptTool> | null) => {
    setEdited(index, true);
    setOptions(index, {
      ...options,
      functions: undefined,
      function_call: undefined,
      tools: tools?.length ? tools.toReversed() : undefined,
      tool_choice: tools?.length ? options.tool_choice ?? 'auto' : undefined,
    });
  };
  const prompt = usePlaygroundStore(useDeep((state) => state.prompts[index]));
  const { modelId } = getModelForPrompt(prompt.manifest);
  const setIsStreaming = usePlaygroundStore(
    useDeep((state) => state.setIsStreaming)
  );

  return (
    <>
      <Tooltip
        title={
          hasOutputSchema
            ? 'Cannot use tools while output schema is enabled'
            : !toolsSupportedForModel
            ? 'This model does not support tools'
            : 'Add a tool to give the model access to functions it can call'
        }
        side="bottom"
      >
        <Button
          data-testid="playground-add-tool"
          onClick={() => setIsModalOpen(true)}
          disabled={hasOutputSchema || !toolsSupportedForModel}
          leftDecorator={tools.length === 0 ? PlusIcon : undefined}
          variant="outlined"
          color="secondary"
          tagText={tools.length ? tools.length.toString() : undefined}
        >
          {tools.length ? pluralize('Tool', tools.length) : 'Tool'}
        </Button>
      </Tooltip>
      {isModalOpen && (
        <ToolModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          tools={tools}
          setTools={setTools}
          modelId={modelId}
          setIsStreaming={setIsStreaming}
        />
      )}
    </>
  );
};
